import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useSelector } from "react-redux";

import "./GlobalRightsResponse.scss";

import { Typography } from "../../../components/__common/Typography/Typography";
import { Col, Container, Row } from "../../../components/Grid/Grid";
import { Svg, SVG_ICONS } from "../../../components/__common/Svg/Svg";
import { LinkButton, TTSButton } from "../../../components/__common/_controls";
import StaticModal from "../../../components/__common/StaticModal/StaticModal";
import {
  CBType,
  Checkbox,
} from "../../../components/__common/_controls/Checkbox/Checkbox";
import GoogleAnalytics from "../../../utils/GoogleAnalytics";

import { useWindowSize } from "../../../hooks/window-hooks";
import { ROUTES } from "../../../configs/routes";
import { ApplicationState } from "../../../store";
import { MyDataState } from "../../../store/reducers/mydata-reducer";
import { GlobalSiteLinkPopup } from "../../../components/GlobalSiteLinkPopup/GlobalSiteLinkPopup";
import { PopupPortal } from "../../../components/__common/PopupPortal/PopupPortal";
import {
  GpsCountry,
  GpsRegion,
  GPS_REGION_CODE,
} from "../../../types/gps-region";
import { GpsRegions } from "../../../data/gps-regions";
import {
  EVENT_CATEGORY_GPS,
  EVENT_GPS_ACTION_LIST,
  EVENT_GPS_ACTION_SDAP_SITE,
} from "../../../constants/analytics";

import GPSMap from "./GPSMap";
import GPSMapMobile from "./GPSMapMobile";
import { GpsListModal } from "./GpsListModal";

// ── Sub-components ──────────────────────────────────────────────

/** Page title + description with TTS button. */
const TitleSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="global-rights__title-section">
      <Typography
        variant="h2"
        className="global-rights__title-section__title sharp-sans"
      >
        {t("PRIVACY_GRRS_PAGE_TITLE")}
        <TTSButton
          value={`${t("PRIVACY_GRRS_PAGE_TITLE")}. ${t("PRIVACY_GRRS_PAGE_DESC")}`}
        />
      </Typography>
      <Typography
        variant="body1"
        weight="w400"
        className="global-rights__title-section__sub-title"
      >
        {t("PRIVACY_GRRS_PAGE_DESC")}
      </Typography>
    </div>
  );
};

/** Map subtitle with two Trans-based links. */
const MapSubtitle: React.FC<{ onOpenModal: () => void }> = ({
  onOpenModal,
}) => {
  const { t } = useTranslation(); // ← Add this
  return (
    <div className="global-rights__map-container__title">
      <Typography
        variant="body2"
        className="global-rights__map-container__title__sub"
      >
        <Trans
          i18nKey="PRIVACY_GRRS_INTEGRATED_SITE_TEXT2"
          components={{
            a: (
              // eslint-disable-next-line
              <a
                href={ROUTES.ROOT}
                data-testid="gps-action-sdap"
                onClick={() => {
                  GoogleAnalytics.triggerEvent(
                    EVENT_CATEGORY_GPS,
                    EVENT_GPS_ACTION_SDAP_SITE
                  );
                }}
              />
            ),
          }}
        />
      </Typography>
      <Typography
        variant="body2"
        className="global-rights__map-container__title__sub"
      >
        <Trans
          i18nKey="PRIVACY_GRRS_INTEGRATED_SITE_TEXT3"
          components={{
            a: (
              // eslint-disable-next-line
              <a
                href={window.location.href}
                data-testid="anchor-gps-modal"
                onClick={(e) => {
                  e.preventDefault();
                  onOpenModal();
                }}
              />
            ),
          }}
        />
      </Typography>
    </div>
  );
};

/** Map view: renders desktop or mobile map based on viewport. */
const MapView: React.FC<{
  isMobile: boolean;
  onRegionClick: (code: GPS_REGION_CODE) => void;
}> = ({ isMobile, onRegionClick }) => {
  if (isMobile) {
    return (
      <div className="gpsMapMobile">
        <GPSMapMobile onRegionClick={onRegionClick} />
      </div>
    );
  }
  return (
    <Container className="global-rights__selected-region">
      <Row>
        <Col lg={12} sm={12}>
          <div className="svg global-rights__map-container__map">
            <GPSMap onRegionClick={onRegionClick} />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

/** Selected region detail: region image + country checklist. */
const SelectedRegionView: React.FC<{
  region: GpsRegion;
  onBack: () => void;
  onCountrySelect: (country: GpsCountry) => void;
}> = ({ region, onBack, onCountrySelect }) => {
  const { t } = useTranslation();
  return (
    <Container className="global-rights__selected-region">
      <Row>
        <Col lg={6} sm={12}>
          <div className="svg global-rights__map-container__map">
            <LinkButton
              title={region.name}
              to="#"
              className="region-title"
              iconPosition="left"
              testId="region-link-btn"
              icon={SVG_ICONS.ARROW_RIGHT}
              underlined={false}
              size="lg"
              iconSize={24}
              svgIconToolTip={t("PRIVACY_GPS_BACK_TO_MAP_ALT_TEXT")}
              onClick={onBack}
            />
            {region.image && (
              <Svg
                icon={region.image}
                className="svg global-rights__map-container__map__selected"
              />
            )}
          </div>
        </Col>
        <Col lg={6} sm={12}>
          <StaticModal show autoHeight id="country-list">
            <StaticModal.Header onHide={onBack}>
              <StaticModal.Title>
                {t("PRIVACY_GRRS_SELECT_COUNTRY")}
              </StaticModal.Title>
            </StaticModal.Header>
            <StaticModal.Body>
              <div className="global-rights__link-container">
                <Container>
                  <Row>
                    {region.countries.map((country) => (
                      <Col
                        md={6}
                        sm={12}
                        className="global-rights__link-container__checkbox"
                        key={country.code}
                      >
                        <Checkbox
                          key={country.code}
                          title={country.name}
                          checkboxTestid="region-checkbox"
                          name="checkbox-withoutSelectBg"
                          type={CBType.withoutSelectBg}
                          onClick={() => onCountrySelect(country)}
                          svgIcon={SVG_ICONS.ARROW_RIGHT}
                        />
                      </Col>
                    ))}
                  </Row>
                </Container>
              </div>
            </StaticModal.Body>
          </StaticModal>
        </Col>
      </Row>
    </Container>
  );
};

// ── Main component ──────────────────────────────────────────────

export const GlobalRightsResponse: React.FC = () => {
  const { isMobile } = useWindowSize();

  const [isToastOpen, setToastOpen] = useState(true);
  const [isGpsListModalOpen, setIsGpsListModalOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<GpsRegion | undefined>();

  const { isGlobalSiteLinkOpen } = useSelector<ApplicationState, MyDataState>(
    (state) => state.myData
  );

  // ── Handlers ───────────────────────────────────────────────

  const handleRegionClick = (regionCode: GPS_REGION_CODE) => {
    const found = GpsRegions.find((r) => r.code === regionCode);
    if (found) setSelectedRegion(found);
  };

  const handleCountrySelect = (country: GpsCountry) => {
    GoogleAnalytics.triggerEvent(
      EVENT_CATEGORY_GPS,
      `${EVENT_GPS_ACTION_LIST}-${country.code}`
    );
    window.open(country.link, "_blank");
  };

  // ── Effects ────────────────────────────────────────────────

  // Scroll to country list on mobile when a region is selected
  useEffect(() => {
    if (selectedRegion && isMobile) {
      document
        .getElementById("country-list")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedRegion, isMobile]);

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="global-rights">
      {/* Title section */}
      <div className="container-wrapper">
        {isToastOpen && isGlobalSiteLinkOpen && (
          <PopupPortal>
            <GlobalSiteLinkPopup
              testId="global-site-link-popup"
              onHide={() => setToastOpen(false)}
            />
          </PopupPortal>
        )}
        <Container>
          <TitleSection />
        </Container>
      </div>

      {/* Map + country selection */}
      <div className="global-rights__map-container">
        <Container>
          <MapSubtitle onOpenModal={() => setIsGpsListModalOpen(true)} />

          {selectedRegion ? (
            <SelectedRegionView
              region={selectedRegion}
              onBack={() => setSelectedRegion(undefined)}
              onCountrySelect={handleCountrySelect}
            />
          ) : (
            <MapView isMobile={isMobile} onRegionClick={handleRegionClick} />
          )}
        </Container>
      </div>

      {/* GPS list modal */}
      <GpsListModal
        isOpen={isGpsListModalOpen}
        onClose={() => setIsGpsListModalOpen(false)}
      />
    </div>
  );
};
